import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Calendar,
  List,
  LayoutGrid,
  ChevronDown,
  ChevronRight,
  Clock,
  User,
  MoreHorizontal,
  Trash2,
  CheckCircle2,
  Circle,
  AlertCircle,
} from 'lucide-react';
import { cn, formatDate } from '../lib/utils';
import { Task, TaskStatus, TaskPriority, tasksApi, TaskFilters } from '../lib/api';

interface TaskBoardProps {
  documentId?: string;
  view?: 'list' | 'board' | 'calendar';
}

const STATUS_CONFIG: Record<TaskStatus, { label: string; labelAr: string; color: string; bgColor: string }> = {
  todo: { label: 'To Do', labelAr: 'للتنفيذ', color: 'text-slate-600', bgColor: 'bg-slate-100' },
  in_progress: { label: 'In Progress', labelAr: 'قيد التنفيذ', color: 'text-blue-600', bgColor: 'bg-blue-100' },
  done: { label: 'Done', labelAr: 'منجز', color: 'text-green-600', bgColor: 'bg-green-100' },
  cancelled: { label: 'Cancelled', labelAr: 'ملغى', color: 'text-gray-400', bgColor: 'bg-gray-100' },
};

const PRIORITY_CONFIG: Record<TaskPriority, { label: string; labelAr: string; color: string; icon: typeof AlertCircle }> = {
  urgent: { label: 'Urgent', labelAr: 'عاجل', color: 'text-red-500', icon: AlertCircle },
  high: { label: 'High', labelAr: 'مرتفعة', color: 'text-orange-500', icon: ChevronRight },
  medium: { label: 'Medium', labelAr: 'متوسطة', color: 'text-yellow-500', icon: ChevronRight },
  low: { label: 'Low', labelAr: 'منخفضة', color: 'text-gray-400', icon: ChevronDown },
};

export default function TaskBoard({ documentId, view = 'list' }: TaskBoardProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language as 'en' | 'ar';
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<'list' | 'board' | 'calendar'>(view);
  const [filters, setFilters] = useState<TaskFilters>({ parentOnly: true });
  const [newTaskTitle, setNewTaskTitle] = useState('');

  // Fetch tasks
  useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);
      try {
        const response = await tasksApi.list({ ...filters, documentId });
        setTasks(response.tasks);
      } catch (error) {
        console.error('Failed to fetch tasks:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTasks();
  }, [filters, documentId]);

  // Create task
  const handleCreateTask = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newTaskTitle.trim()) return;

    try {
      const response = await tasksApi.create({
        title: newTaskTitle,
        documentId,
        status: 'todo',
        priority: 'medium',
      });
      setTasks([...tasks, response.task]);
      setNewTaskTitle('');
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  // Update task status
  const handleStatusChange = async (taskId: string, newStatus: TaskStatus) => {
    try {
      await tasksApi.update(taskId, { status: newStatus });
      setTasks(tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  // Delete task
  const handleDelete = async (taskId: string) => {
    try {
      await tasksApi.delete(taskId);
      setTasks(tasks.filter(t => t.id !== taskId));
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  // Group tasks by status for board view
  const groupedTasks = {
    todo: tasks.filter(t => t.status === 'todo'),
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    done: tasks.filter(t => t.status === 'done'),
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="notion-heading-2 flex items-center gap-2">
          <CheckCircle2 className="h-6 w-6 text-primary" />
          {lang === 'ar' ? 'المهام' : 'Tasks'}
        </h2>

        {/* View switcher */}
        <div className="flex items-center gap-2 bg-secondary rounded-lg p-1">
          <button
            onClick={() => setCurrentView('list')}
            className={cn('btn-ghost h-8 px-3', currentView === 'list' && 'bg-background shadow-sm')}
          >
            <List className="h-4 w-4" />
          </button>
          <button
            onClick={() => setCurrentView('board')}
            className={cn('btn-ghost h-8 px-3', currentView === 'board' && 'bg-background shadow-sm')}
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setCurrentView('calendar')}
            className={cn('btn-ghost h-8 px-3', currentView === 'calendar' && 'bg-background shadow-sm')}
          >
            <Calendar className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select
          value={filters.status || ''}
          onChange={(e) => setFilters({ ...filters, status: e.target.value as TaskStatus || undefined })}
          className="input h-8 text-sm w-auto"
        >
          <option value="">{lang === 'ar' ? 'كل الحالات' : 'All statuses'}</option>
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{lang === 'ar' ? config.labelAr : config.label}</option>
          ))}
        </select>
        <select
          value={filters.priority || ''}
          onChange={(e) => setFilters({ ...filters, priority: e.target.value as TaskPriority || undefined })}
          className="input h-8 text-sm w-auto"
        >
          <option value="">{lang === 'ar' ? 'كل الأولويات' : 'All priorities'}</option>
          {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{lang === 'ar' ? config.labelAr : config.label}</option>
          ))}
        </select>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          {lang === 'ar' ? 'جاري التحميل...' : 'Loading...'}
        </div>
      ) : currentView === 'list' ? (
        /* List View */
        <div className="space-y-2">
          {/* Quick add */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateTask()}
              placeholder={lang === 'ar' ? 'مهمة جديدة...' : 'New task...'}
              className="input flex-1"
              dir="auto"
            />
            <button onClick={() => handleCreateTask()} className="btn-primary">
              <Plus className="h-4 w-4" />
            </button>
          </div>

          {/* Task list */}
          {tasks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>{lang === 'ar' ? 'لا توجد مهام' : 'No tasks yet'}</p>
            </div>
          ) : (
            tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                lang={lang}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />
            ))
          )}
        </div>
      ) : currentView === 'board' ? (
        /* Board/Kanban View */
        <div className={cn('grid grid-cols-1 md:grid-cols-3 gap-4', lang === 'ar' && 'md:flex-row-reverse')}>
          {(['todo', 'in_progress', 'done'] as const).map((status) => (
            <div key={status} className="bg-secondary/50 rounded-lg p-3 min-h-[300px]">
              <div className={cn('flex items-center gap-2 mb-3 px-2', STATUS_CONFIG[status].color)}>
                <span className={cn('h-2 w-2 rounded-full', STATUS_CONFIG[status].bgColor)} />
                <span className="font-medium text-sm">
                  {lang === 'ar' ? STATUS_CONFIG[status].labelAr : STATUS_CONFIG[status].label}
                </span>
                <span className="text-xs text-muted-foreground ms-auto">
                  {groupedTasks[status].length}
                </span>
              </div>
              
              <div className="space-y-2">
                {groupedTasks[status].map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    lang={lang}
                  />
                ))}
                
                {/* Add to column */}
                {status === 'todo' && (
                  <div className="p-2 text-center text-xs text-muted-foreground">
                    {lang === 'ar' ? 'اسحب المهام هنا' : 'Drag tasks here'}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Calendar View (simplified) */
        <div className="text-center py-12 text-muted-foreground">
          <Calendar className="h-12 w-12 mx-auto mb-4 opacity-20" />
          <p>{lang === 'ar' ? 'عرض التقويم قريباً' : 'Calendar view coming soon'}</p>
        </div>
      )}
    </div>
  );
}

// Task Row Component (for list view)
function TaskRow({
  task,
  lang,
  onStatusChange,
  onDelete,
}: {
  task: Task;
  lang: 'en' | 'ar';
  onStatusChange: (id: string, status: TaskStatus) => void;
  onDelete: (id: string) => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const priorityConfig = PRIORITY_CONFIG[task.priority];
  const PriorityIcon = priorityConfig.icon;

  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'done';

  return (
    <div className={cn(
      'group flex items-center gap-3 p-3 rounded-lg border hover:shadow-sm transition-all',
      task.status === 'done' && 'opacity-60'
    )}>
      {/* Status checkbox */}
      <button
        onClick={() => onStatusChange(task.id, task.status === 'done' ? 'todo' : 'done')}
        className={cn('shrink-0', task.status === 'done' ? 'text-green-500' : 'text-muted-foreground')}
      >
        {task.status === 'done' ? (
          <CheckCircle2 className="h-5 w-5" />
        ) : (
          <Circle className="h-5 w-5 hover:text-primary" />
        )}
      </button>

      {/* Title */}
      <div className="flex-1 min-w-0">
        <div className={cn('font-medium truncate', task.status === 'done' && 'line-through')}>
          {task.title}
        </div>
        {task.subtaskCount && task.subtaskCount > 0 && (
          <div className="text-xs text-muted-foreground">
            {task.completedSubtaskCount}/{task.subtaskCount} {lang === 'ar' ? 'مهام فرعية' : 'subtasks'}
          </div>
        )}
      </div>

      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="hidden sm:flex gap-1">
          {task.tags.slice(0, 2).map((tag) => (
            <span
              key={tag.name}
              className="px-2 py-0.5 rounded text-xs font-medium"
              style={{ backgroundColor: tag.color + '20', color: tag.color }}
            >
              {tag.name}
            </span>
          ))}
        </div>
      )}

      {/* Priority */}
      <div className={cn('hidden sm:flex items-center gap-1 text-xs', priorityConfig.color)}>
        <PriorityIcon className="h-3.5 w-3.5" />
      </div>

      {/* Due date */}
      {task.dueDate && (
        <div className={cn(
          'hidden sm:flex items-center gap-1 text-xs',
          isOverdue ? 'text-red-500' : 'text-muted-foreground'
        )}>
          <Clock className="h-3.5 w-3.5" />
          <span>{formatDate(task.dueDate, lang)}</span>
        </div>
      )}

      {/* Assignee */}
      {task.assigneeName && (
        <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground">
          <User className="h-3.5 w-3.5" />
          <span>{task.assigneeName}</span>
        </div>
      )}

      {/* Menu */}
      <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="btn-icon h-7 w-7 opacity-0 group-hover:opacity-100"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
        
        {showMenu && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
            <div className="dropdown-content absolute end-0 top-full mt-1 w-40 z-50">
              <button
                onClick={() => { onStatusChange(task.id, 'in_progress'); setShowMenu(false); }}
                className="menu-item"
              >
                {lang === 'ar' ? 'قيد التنفيذ' : 'Start working'}
              </button>
              <button
                onClick={() => { onDelete(task.id); setShowMenu(false); }}
                className="menu-item text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                {lang === 'ar' ? 'حذف' : 'Delete'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Task Card Component (for board view)
function TaskCard({
  task,
  lang,
}: {
  task: Task;
  lang: 'en' | 'ar';
}) {
  const priorityConfig = PRIORITY_CONFIG[task.priority];
  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'done';

  return (
    <div className="bg-card rounded-lg border p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
      <div className="font-medium text-sm mb-2 line-clamp-2">{task.title}</div>
      
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        {/* Priority dot */}
        <div className={cn('h-2 w-2 rounded-full', priorityConfig.color.replace('text-', 'bg-'))} />
        
        {/* Due date */}
        {task.dueDate && (
          <span className={isOverdue ? 'text-red-500' : ''}>
            {formatDate(task.dueDate, lang)}
          </span>
        )}
        
        {/* Subtasks */}
        {task.subtaskCount && task.subtaskCount > 0 && (
          <span>
            {task.completedSubtaskCount}/{task.subtaskCount}
          </span>
        )}
      </div>
      
      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex gap-1 mt-2 flex-wrap">
          {task.tags.map((tag) => (
            <span
              key={tag.name}
              className="px-1.5 py-0.5 rounded text-[10px] font-medium"
              style={{ backgroundColor: tag.color + '20', color: tag.color }}
            >
              {tag.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
